import React, { CSSProperties, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Tooltip,
  Modal,
  Upload,
  UploadFile,
  UploadProps,
  Progress,
  Space,
  message,
} from "antd";
import {
  CombustionManualSamling,
  StaticColumnsType,
  ManualEmissionSampling,
  SamplingEmission,
  RowData,
  DataTable,
  FileDetails,
} from "src/entity/manual-sampling/manual-sampling.entity";
import FormItem from "antd/es/form/FormItem";
import { CheckCircleOutlined, CloudUploadOutlined, DeleteOutlined, DownloadOutlined, FileFilled, FileOutlined } from "@ant-design/icons";
import Dragger from "antd/es/upload/Dragger";
import FileDescriptionList from "./FileDescriptionList";
import { componentPalette } from "src/theme/component-palette";
import {
  deleteFileManualSampling,
  downloadFileSampling,
  getFileByCombustionId,
  uploadFileManualSamplingCombust,
} from "src/services/combustion.service";
import FileIcon from "./FileIcon";
import "./File.css";
import axios from "axios";
import * as moment from "moment-timezone";
import { ReportStatusEnum } from "src/entity/report-voc-emission/report-voc-emission.entity";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import { onKeyDownNumberWithFixedDecimal } from "src/utils/key-down-validate";
import { formatNumber, formatOnlyComma } from "src/utils/process-number";
import { convertEmissionWord } from "src/entity/common/global.entity";

const ManualSamplingModal = (props: {
  open: boolean;
  companyId: string;
  year: number | undefined;
  initialData: CombustionManualSamling;
  dataTable: DataTable;
  loadingSave: boolean;
  onCancelManualSam: () => void;
  onSaveManualSam: (data: CombustionManualSamling) => void;
}) => {
  const { open, companyId, year, initialData, dataTable, loadingSave, onCancelManualSam, onSaveManualSam } = props;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [combustionData, setCombustionData] = useState<CombustionManualSamling>(initialData);
  const [fileDetails, setFileDetails] = useState<FileDetails[]>();
  const [dataTransform, setDataTransform] = useState(dataTable);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const acceptFileType = `.pdf,.PDF,.jpg,.JPG,.png,.PNG,.jpeg,.JPEG,.docx,.DOCX,.xlsx,.XLSX,.pptx,.PPTX,.ppt,.PPT`;

  const max_width: CSSProperties = { width: "100%" };

  const staticColumnsKey: StaticColumnsType[] = [
    {
      label: "Stack ID",
      key: "stackId",
    },
    {
      label: "Source ID",
      key: "sourceId",
    },
    {
      label: "Area",
      key: "area",
    },
  ];

  const [selectedMonth, setSelectedMonth] = useState<keyof ManualEmissionSampling>("jan");

  type MonthManual = keyof ManualEmissionSampling;
  const months: MonthManual[] = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

  const handleSaveCombustion = async () => {
    try {
      await form.validateFields();
      await onSaveManualSam(combustionData);
    } catch (error) {
      console.error(error);
    }
  };

  const checkStatusReport = (month: keyof ManualEmissionSampling | undefined) => {
    let isDisabled = false;
    if (month) {
      if (combustionData.manualEmissionSampling[month].reportStatus == ReportStatusEnum.PROCESSING) {
        isDisabled = false;
      } else {
        isDisabled = true;
      }
    }
    return isDisabled;
  };

  useEffect(() => {
    form.setFieldsValue(combustionData);
    handleFetchListFile();
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      handleChangeDisplay(selectedMonth);
      const tmpCombustion = combustionData;
      months.forEach((month) => {
        const resFile = fileDetails?.filter((item) => item.month === month);
        if (resFile && resFile.length > 0) {
          const updateFiles = resFile.map((file) => String(file._id));
          setCombustionData((prevState) => ({
            ...prevState,
            manualEmissionSampling: {
              ...prevState.manualEmissionSampling,
              [month]: {
                ...prevState.manualEmissionSampling[month],
                files: updateFiles,
              },
            },
          }));
        } else {
          setCombustionData((prevState) => ({
            ...prevState,
            manualEmissionSampling: {
              ...prevState.manualEmissionSampling,
              [month]: {
                ...prevState.manualEmissionSampling[month],
                files: [],
              },
            },
          }));
        }
      });
    }
  }, [fileDetails]);

  useEffect(() => {
    form.setFieldsValue(combustionData);
  }, [combustionData, form]);

  const handleInputChange = (parameter: keyof SamplingEmission, month: keyof ManualEmissionSampling, value: number | null) => {
    let combustParam: keyof SamplingEmission = parameter;
    if (String(combustParam) === "Operating Hours") {
      combustParam = "operatingHours";
    } else {
      combustParam = combustParam.toLocaleLowerCase() as keyof SamplingEmission;
    }

    const tmpDataTable: RowData[] = dataTable;
    let idx = tmpDataTable.findIndex((el) => el.parameter === parameter);
    if (idx != -1 && tmpDataTable[idx]) {
      tmpDataTable[idx][month] = value;
    }

    setDataTransform([...tmpDataTable]);
    setCombustionData((prevData) => ({
      ...prevData,
      manualEmissionSampling: {
        ...prevData.manualEmissionSampling,
        [month]: {
          ...prevData.manualEmissionSampling[month],
          emission: {
            ...prevData.manualEmissionSampling[month].emission,
            [combustParam]: {
              ...prevData.manualEmissionSampling[month].emission[combustParam],
              value: value,
            },
          },
        },
      },
    }));
  };

  const handleFetchListFile = async () => {
    try {
      setLoading(true);
      const _id = combustionData._id;
      if (_id) {
        let response = await getFileByCombustionId(companyId, _id);
        if (response.data.code === 200) {
          const files = response.data.data;
          setLoading(false);
          setFileDetails(files);
          const fileRefer = files.filter((item: any) => item.month === selectedMonth);
          const idxRefer = dataTransform.findIndex((item) => item.key == "reference");
          if (idxRefer != -1) {
            let tmpDataTrans = dataTransform;
            tmpDataTrans[idxRefer][selectedMonth] = fileRefer;
            setDataTransform(tmpDataTrans);
          }
        } else {
          setLoading(false);
          message.error("Failed to fetch the file.");
        }
      }
    } catch (error: any) {
      message.error("Failed to fetch the file");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeDisplay = (month: string) => {
    const files = fileDetails?.filter((file) => file.month === month);
    if (files != undefined) {
      let fileItem: UploadFile[] = files?.map((file) => ({
        uid: file._id,
        name: file.originalName,
        status: "done",
        url: file.fullUrl,
        size: file.fileSize,
        lastModified: moment.utc(file.updatedAt).clone().utcOffset(7).tz("Asia/Bangkok").unix(),
        type: file.fileType,
      }));
      setFileList(fileItem);
    }
  };

  const handleChangeMonth = (month: keyof ManualEmissionSampling) => {
    setSelectedMonth(month);
    handleChangeDisplay(month);
  };

  const onRemove = async (file: UploadFile) => {
    try {
      if (file.status === "error") {
        setFileList((prevList) => prevList.filter((item) => item.uid !== file.uid));
      } else {
        setLoading(true);
        const _id = file.uid;
        let response = await deleteFileManualSampling(companyId, _id);
        if (response.data.code == 200) {
          setLoading(false);
          handleFetchListFile();
          message.success("Delete Successfully.");
        }
      }
    } catch (error) {
      setLoading(false);
      message.error("Delete Fail.");
    }
  };

  const beforeUpload: UploadProps["beforeUpload"] = (file: any, newFileList) => {
    const splitFileName = file.name.split(".");
    const typeFile = splitFileName[splitFileName.length - 1];
    const isCorrectType = acceptFileType.includes(typeFile);
    if (newFileList.findIndex((f) => f.uid === file.uid) + 1 + fileList.length > 3) {
      return Upload.LIST_IGNORE;
    }
    if (!isCorrectType) {
      message.error("Invalid file type");
      return Upload.LIST_IGNORE;
    }

    const isLt50M = file.size / 1024 / 1024 < 10;
    if (!isLt50M) {
      message.error("File must smaller than 10MB");
      return Upload.LIST_IGNORE;
    }
  };

  const onDownloadFile = async (file: UploadFile) => {
    try {
      const responeBlob = await downloadFileSampling(companyId, file.uid);
      if (responeBlob.data.code === 200) {
        const urlDownload = responeBlob.data.data;
        if (urlDownload && file.type && file.name) {
          downloadURI(file.name, file.type, urlDownload);
        }
      } else {
        message.error("Download Fail");
      }
    } catch (error) {
      message.error("Download Fail");
    }
  };

  const downloadURI = (fileName: string, fileType: string, urlDownload: string) => {
    axios({
      method: "get",
      url: urlDownload,
      responseType: "blob",
    })
      .then((response) => {
        const link: any = document.createElement("a");
        link.href = window.URL.createObjectURL(new Blob([response.data], { type: fileType }));
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        setTimeout(function () {
          window.URL.revokeObjectURL(link);
        }, 200);
      })
      .catch(() => {
        message.error("Download Fail");
      });
  };

  const beforeRemoveFile = (actions: any) => {
    Modal.confirm({
      title: "Confirm to Delete?",
      content: "Are you sure you want to delete this file?",
      okText: "Yes",
      okButtonProps: { style: { backgroundColor: componentPalette.primary } },
      cancelButtonProps: { style: { borderColor: componentPalette.primary, color: componentPalette.primary } },
      onOk: () => {
        actions.remove();
      },
    });
  };

  const formatBytes = (bytes: any, decimals = 2) => {
    if (!+bytes) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  const renderFileListUploaded = (_originNode: any, file: UploadFile, _fileList: any, actions: any) => {
    const errorBorderStyle = file?.status == "error" ? { border: `1px solid ` } : {};
    const sub_detail_text: CSSProperties = {
      fontSize: 12,
      color: "grey",
    };

    const extendsion: string = file?.name ? file.name.split(".")[file.name.split(".").length - 1] : "";

    const fileAlready = file && file?.status == "done";

    const propsDownload = fileAlready ? { onClick: actions.download } : { style: { color: "grey" } };
    const propsRemove = fileAlready ? { onClick: () => beforeRemoveFile(actions) } : { style: { color: "grey" } };

    return (
      <>
        {fileList[0] === file && (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 25 }}>
            <p>รายการที่อัพโหลด (List of uploaded files)</p>
          </div>
        )}
        <Row style={{ ...max_width, marginTop: 10 }} key={file.name}>
          <Col
            span={24}
            style={{
              border: `1px solid grey`,
              borderRadius: 10,
              padding: "7.5px 7.5px",
              ...errorBorderStyle,
              boxShadow: `1px 1px 2px grey`,
            }}
          >
            <Row align={"middle"}>
              <Col span={3}>
                <Card style={{ width: 35, height: 40, display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <FileIcon fileName={file.name} />
                </Card>
              </Col>

              <Col span={17} style={{ textAlign: "left" }}>
                <Col style={{ ...max_width }}>
                  <span>{file?.name || "no name"}</span>
                </Col>
                {file?.status && file.status != "done" ? (
                  <Progress
                    percent={file.percent}
                    status={file?.status == "error" ? "exception" : file?.percent == 100 ? "success" : "normal"}
                    format={(percent) => {
                      return percent == 100 ? (
                        <CheckCircleOutlined style={{ borderRadius: 7, fontSize: 14 }} />
                      ) : percent ? (
                        Math.round(percent) + "%"
                      ) : (
                        ""
                      );
                    }}
                  />
                ) : (
                  <>
                    <Row>
                      <Col span={18}>
                        <span style={{ fontSize: "12px", color: "grey" }}>
                          {file.lastModified ? moment.unix(file.lastModified).format("YYYY-MM-DD HH:mm:ss") : ""}
                        </span>
                      </Col>
                      <Col span={6}>
                        <span style={sub_detail_text}>{formatBytes(file.size)}</span>
                      </Col>
                    </Row>
                  </>
                )}
              </Col>
              <Col span={4}>
                <div>
                  <Space>
                    <Button type="text" size="small" icon={<DownloadOutlined {...propsDownload} />}></Button>
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined {...propsRemove} />}
                      disabled={checkStatusReport(selectedMonth)}
                    ></Button>
                  </Space>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </>
    );
  };

  const onChangeFile: UploadProps["onChange"] = (info) => {
    const { status } = info.file;

    if (status === "uploading") {
      setFileList([...info.fileList]);
    }

    if (status === "done") {
      setLoading(false);
      message.success("Upload Successfully");
    } else if (status === "error") {
      setLoading(false);
      message.success("Upload Fail");
    }
  };

  const customRequest = async (options: any) => {
    const { onSuccess, onError, file, onProgress } = options;
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("files", file);
      const _id = combustionData._id;
      if (_id && selectedMonth && year) {
        setLoading(false);
        const response = await uploadFileManualSamplingCombust(companyId, _id, selectedMonth, year, formData, (event) => {
          if (event.lengthComputable) {
            const percentCompleted = Math.round((event.loaded * 100) / event.total);
            onProgress({ percent: percentCompleted });
          }
        });
        if (response.data.code == 200) {
          const uid = file.uid;
          const idx = fileList.findIndex((file) => file.uid === uid);
          if (idx != -1) {
            handleFetchListFile();
          }
          onSuccess("Upload Success");
        } else {
          onError("Uplaod Fail");
        }
      }
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const getFileIconStyle = (month: string, row: any) => ({
    color: Array.isArray(row[month]) && (row[month] as FileDetails[]).length !== 0 ? componentPalette.primary : "gray",
    fontSize: "24px",
    padding: 0,
    backgroundColor: "transparent",
    border: "none",
  });

  const uploadFileManual: UploadProps = {
    className: "parent",
    accept: ".doc,.docx,.pdf,.xlsx,.xls,.pptx,.ppt,.jpg,.JPG,.jpeg,.JPEG,.png,.PNG",
    style: { border: "2px dashed #4C95EB", height: "20px" },
    multiple: true,
    onChange: onChangeFile,
    customRequest: customRequest,
    beforeUpload: beforeUpload,
    onRemove: onRemove,
    fileList: fileList,
    showUploadList: true,
    onDownload: onDownloadFile,
    listType: "picture",
    itemRender: renderFileListUploaded,
    disabled: checkStatusReport(selectedMonth),
  };

  return (
    <div>
      {open && (
        <Modal
          title={<ModalTitle title="Sampling" />}
          open={open}
          width={1200}
          okText="Save"
          onCancel={onCancelManualSam}
          cancelText="Cancel"
          centered
          bodyStyle={{ height: 700, overflowY: "auto", overflowX: "hidden" }}
          maskClosable={false}
          footer={
            <Row className="w-full border-t" justify={"end"}>
              <Button className="mt-4" key="submit" type="primary" onClick={handleSaveCombustion} loading={loadingSave}>
                Save
              </Button>
            </Row>
          }
        >
          <Form size="large" layout="vertical" autoComplete="off" form={form}>
            <Row gutter={16}>
              {staticColumnsKey.map((column, index) => (
                <Col xs={{ span: 24 }} sm={{ span: 8 }} md={{ span: 8 }} key={index}>
                  <FormItem label={column.label}>
                    <Input disabled value={String(combustionData[column.key])} />
                  </FormItem>
                </Col>
              ))}
            </Row>

            <Row style={{ justifyContent: "center", alignItems: "center", paddingBottom: "25px" }}>
              <Col span={4}>
                <Row gutter={[0, 8]}>
                  <Col span={16}></Col>
                  <Col span={8}>Unit</Col>
                </Row>
              </Col>
              <Col span={20}>
                <Row gutter={[0, 8]}>
                  {months.map((month) => (
                    <>
                      <Col span={2} style={{ textAlign: "center" }}>
                        {month.charAt(0).toUpperCase() + month.slice(1)}
                      </Col>
                    </>
                  ))}
                </Row>
              </Col>
            </Row>

            {dataTransform.map((row, index) => (
              <div key={index} style={{ marginBottom: 16 }}>
                <Row gutter={[0, 8]} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <Col span={4}>
                    <Row gutter={[0, 8]}>
                      <Col span={16}>
                        <label>{convertEmissionWord(row.parameter)}</label>
                      </Col>
                      <Col span={8}>
                        <label>{row.unit}</label>
                      </Col>
                    </Row>
                  </Col>

                  <Col span={20}>
                    <Row gutter={[8, 8]}>
                      {months.map((month, index) => {
                        return row.key === "reference" ? (
                          <>
                            <Col span={2} key={index} style={{ textAlign: "center" }}>
                              <span>{Array.isArray(row[month])}</span>
                              {Array.isArray(row[month]) && (
                                <>
                                  <Tooltip
                                    style={{ boxShadow: `1px 1px 2px grey` }}
                                    overlayInnerStyle={{ color: "black" }}
                                    color="white"
                                    placement="topLeft"
                                    title={
                                      (row[month] as FileDetails[]).length != 0 ? (
                                        <FileDescriptionList files={row[month] as FileDetails[]} />
                                      ) : (
                                        "No Data"
                                      )
                                    }
                                    arrow
                                  >
                                    <Upload {...uploadFileManual} showUploadList={false} disabled={checkStatusReport(month)}>
                                      <Button
                                        onClick={() => handleChangeMonth(month)}
                                        type="link"
                                        // disabled={checkStatusReport(month)}
                                        icon={
                                          month === selectedMonth ? (
                                            <FileFilled style={getFileIconStyle(month, row)} />
                                          ) : (
                                            <FileOutlined style={getFileIconStyle(month, row)} />
                                          )
                                        }
                                      />
                                    </Upload>
                                  </Tooltip>
                                </>
                              )}
                            </Col>
                          </>
                        ) : (
                          <Col span={2} key={index}>
                            <InputNumber
                              min={0}
                              size="large"
                              step={1}
                              style={{ width: "100%", fontSize: "10px" }}
                              disabled={checkStatusReport(month)}
                              value={row[month] != undefined ? Number(row[month]) : null}
                              onChange={(value) => {
                                handleInputChange(row.parameter as keyof SamplingEmission, month, value);
                              }}
                              onKeyDown={(e) => {
                                onKeyDownNumberWithFixedDecimal(e, row.key === "operatingHours" ? 2 : 6);
                              }}
                            />
                          </Col>
                        );
                      })}
                    </Row>
                  </Col>
                </Row>
              </div>
            ))}

            <Row gutter={[20, 20]} style={{ paddingLeft: "200px", paddingRight: "50px" }}>
              <Col span={24}>
                <Dragger {...uploadFileManual}>
                  <p className="ant-upload-drag-icon">
                    <CloudUploadOutlined />
                  </p>
                  <p className="ant-upload-text">คลิกหรือลากไฟล์เพื่ออัพโหลด</p>
                  <p className="ant-upload-hint" style={{ textAlign: "left" }}>
                    ข้อจำกัด
                  </p>
                  <p className="ant-upload-hint" style={{ textAlign: "left" }}>
                    1. อัพโหลดได้เฉพาะไฟล์ที่มีนามสกุล .doc, .docx, .pdf, .xlsx, .xls, .pptx, .ppt, .jpg, .JPG, .jpeg, .JPEG, .png, .PNG
                  </p>
                  <p className="ant-upload-hint" style={{ textAlign: "left" }}>
                    2. อัพโหลดได้เฉพาะไฟล์ที่มีขนาด 10 MB
                  </p>
                  <p className="ant-upload-hint" style={{ textAlign: "left" }}>
                    3. อัพโหลดไฟล์ได้สูงสุด 3 ไฟล์
                  </p>
                </Dragger>
              </Col>
            </Row>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default ManualSamplingModal;
{
}
